'use client';

import { XMarkIcon } from '@heroicons/react/20/solid';
import { useCallback } from 'react';
import Markdown from 'react-markdown';
import { useSessionStorage } from 'usehooks-ts';

import { useGetActiveFlashes } from '@/lib/react-query/FlashQueries';
import { cn } from '@/lib/utils';

const isSafeUrl = (url: string) => {
  try {
    const parsed = new URL(url, window.location.origin);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

type Props = {
  className?: string;
};

const FlashMessage = ({ className = '' }: Props) => {
  const { data: flashes = [] } = useGetActiveFlashes();
  const [value, setValue] = useSessionStorage('show-flash-message', true);

  const flash = value && flashes.length > 0 ? flashes[0] : null;

  const handleDismiss = useCallback(() => {
    setValue(false);
  }, [setValue]);

  return flash ? (
    <div
      className={cn(
        'relative isolate flex items-center gap-x-6 overflow-hidden bg-amber-100 px-6 py-2.5 sm:px-3.5 sm:before:flex-1',
        className,
      )}
    >
      <div
        className="absolute top-1/2 left-[max(-7rem,calc(50%-52rem))] -z-10 -translate-y-1/2 transform-gpu blur-2xl"
        aria-hidden="true"
      >
        <div
          className="aspect-577/310 w-[36.0625rem] bg-linear-to-r from-amber-400 to-yellow-800 opacity-30"
          style={{
            clipPath:
              'polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)',
          }}
        />
      </div>
      <div
        className="absolute top-1/2 left-[max(45rem,calc(50%+8rem))] -z-10 -translate-y-1/2 transform-gpu blur-2xl"
        aria-hidden="true"
      >
        <div
          className="aspect-577/310 w-[36.0625rem] bg-linear-to-r from-amber-400 to-yellow-800 opacity-30"
          style={{
            clipPath:
              'polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)',
          }}
        />
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="text-sm leading-6 text-gray-900">
          <strong className="font-semibold">{flash.title}</strong>
          <svg
            viewBox="0 0 2 2"
            className="mx-2 inline h-0.5 w-0.5 fill-current"
            aria-hidden="true"
          >
            <circle cx={1} cy={1} r={1} />
          </svg>
          <div className="inline-block">
            <Markdown allowedElements={['p', 'strong', 'em', 'a', 'br']} unwrapDisallowed>
              {flash.message}
            </Markdown>
          </div>
        </div>
        {flash.action_label && flash.action_url && isSafeUrl(flash.action_url) ? (
          <a
            href={flash.action_url}
            className="flex-none rounded-full bg-gray-900 px-3.5 py-1 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
          >
            {flash.action_label} <span aria-hidden="true">&rarr;</span>
          </a>
        ) : null}
      </div>
      {flash.allow_close ? (
        <div className="flex flex-1 justify-end">
          <button
            type="button"
            onClick={handleDismiss}
            className="-m-3 p-3 focus-visible:-outline-offset-4"
          >
            <span className="sr-only">Dismiss</span>
            <XMarkIcon className="h-5 w-5 text-gray-900" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <div className="flex flex-1 justify-end" />
      )}
    </div>
  ) : null;
};

export default FlashMessage;
